import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Container,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  IconButton,
  LinearProgress
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { 
  Zap, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Target,
  MoreVertical,
  RefreshCw,
  List
} from 'lucide-react';

// ¡Importamos el nuevo componente ActionPlan!
import ActionPlan from './ActionPlan';

import strategyService from '../../services/AGTUps-product-services/strategyService';

// Función para obtener colores según el modo (claro/oscuro)
const getThemeColors = (mode) => ({
  ...(mode === 'dark'
    ? {
        background: '#1C1C1E',
        text: '#FFFFFF',
        cardBg: '#272729',
        border: '#464649',
        secondaryText: 'rgba(255, 255, 255, 0.7)'
      }
    : {
        background: '#F2F2F7',
        text: '#000000',
        cardBg: '#FFFFFF',
        border: '#C7C7CC',
        secondaryText: '#666666'
      })
});

const StyledPaper = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = getThemeColors(theme.palette.mode);

  return {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.cardBg,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    boxShadow: 'none',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${colors.border}`,
    color: colors.text
  };
});

const OpportunityCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = getThemeColors(theme.palette.mode);

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    marginBottom: theme.spacing(2),
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[2]
    }
  };
});

const ActionButton = styled(Button)(({ theme, variant }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  // Primary button (contained)
  if (variant !== 'outlined') {
    return {
      padding: theme.spacing(1.5, 3),
      borderRadius: theme.spacing(3),
      backgroundColor: isWallpaperMode ? '#FFFFFF' : '#007BFE',
      color: isWallpaperMode ? '#000000' : '#FFFFFF',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      transition: 'all 0.2s',
      marginTop: theme.spacing(3),
      boxShadow: 'none',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#0056b3',
        boxShadow: 'none'
      },
      '&:disabled': {
        backgroundColor: '#ccc'
      }
    };
  }

  // Outlined button
  return {
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.spacing(3),
    backgroundColor: isWallpaperMode ? '#FFFFFF' : 'transparent',
    color: isWallpaperMode ? '#000000' : '#007BFE',
    borderColor: isWallpaperMode ? '#FFFFFF' : '#007BFE',
    border: `1px solid ${isWallpaperMode ? '#FFFFFF' : '#007BFE'}`,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    transition: 'all 0.2s',
    marginTop: theme.spacing(3),
    boxShadow: 'none',
    textTransform: 'none',
    '&:hover': {
      borderColor: isWallpaperMode ? '#FFFFFF' : '#0056b3',
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 123, 254, 0.08)',
      boxShadow: 'none'
    },
    '&:disabled': {
      borderColor: '#ccc',
      color: '#ccc'
    }
  };
});

const StyledIconButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : 'inherit',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.04)'
    }
  };
});

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    '& .MuiDialog-paper': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    }
  };
});

const DialogCloseButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    color: isWallpaperMode ? '#FFFFFF' : '#007BFE',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 123, 254, 0.08)'
    }
  };
});

const Strategy = () => {
  const theme = useTheme();
  const colors = getThemeColors(theme.palette.mode);

  // ------------------------
  // 1) Nuevo estado para la vista
  // ------------------------
  const [view, setView] = useState('overview'); // Puede ser 'overview' o 'action-plan'
  
  // Estados existentes
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [stats, setStats] = useState(null);

  // Cargar estrategias
  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const response = await strategyService.getUserStrategies();
      if (response.success) {
        setStrategies(response.data);
        // Obtener estadísticas
        const statsResponse = await strategyService.getStrategyStats();
        if (statsResponse.success) {
          setStats(statsResponse.data);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generar análisis
  const handleGenerateAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await strategyService.generateAnalysis();
      if (response.success) {
        await fetchStrategies(); // Recargar estrategias después del análisis
        setError('Análisis generado exitosamente');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // Manejar clic en estrategia
  const handleStrategyClick = (strategy) => {
    setSelectedStrategy(strategy);
    setOpenDetailDialog(true);
  };

  // Manejar menú de opciones
  const handleMenuClick = (event, strategy) => {
    event.stopPropagation();
    setSelectedStrategy(strategy);
    setMenuAnchorEl(event.currentTarget);
  };

  // Efecto para cargar data al montar
  useEffect(() => {
    fetchStrategies();
  }, []);

  // Mapear estrategias a oportunidades
  const opportunities = strategies.map(strategy => {
    const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
    return {
      icon: strategy.status === 'COMPLETED' ? <CheckCircle size={20} /> :
            strategy.status === 'IN_PROGRESS' ? <Clock size={20} /> :
            <AlertCircle size={20} />,
      title: strategy.title,
      description: strategy.description,
      status: strategy.status === 'COMPLETED' ? 'Ready' :
              strategy.status === 'IN_PROGRESS' ? 'In Progress' : 'Planned',
      color: strategy.status === 'COMPLETED' ? '#2DD257' :
             strategy.status === 'IN_PROGRESS' ? (isWallpaperMode ? '#FFFFFF' : '#0385FF') : '#F97316',
      progress: strategy.progress
    };
  });

  // ------------------------
  // 2) Lógica para cambiar la vista
  // ------------------------
  // Si la vista es "action-plan", renderizamos el componente ActionPlan
  // y pasamos onBack para volver a 'overview'.
  if (view === 'action-plan') {
    return <ActionPlan onBack={() => setView('overview')} />;
  }

  // Si la vista es 'overview', mostramos el contenido original
  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Zap size={24} color="#F97316" />
                </Box>
                <Typography variant="h6" sx={{ color: colors.text, fontWeight: 500 }}>
                  Growth Strategy Recommendations
                </Typography>
              </Box>
              
              {stats && (
                <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                  {stats.byStatus.COMPLETED} Completed · {stats.byStatus.IN_PROGRESS} In Progress
                </Typography>
              )}
            </Box>

            {/* Loading */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : opportunities.length > 0 ? (
              // Listado de oportunidades
              opportunities.map((opportunity, index) => (
                <OpportunityCard 
                  key={index}
                  onClick={() => handleStrategyClick(strategies[index])}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'start',
                      mb: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {React.cloneElement(opportunity.icon, {
                        color: opportunity.color
                      })}
                      <Typography variant="subtitle1" sx={{ color: colors.text, fontWeight: 500 }}>
                        {opportunity.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: opportunity.color,
                          backgroundColor: `${opportunity.color}15`,
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontWeight: 500
                        }}
                      >
                        {opportunity.status}
                      </Typography>
                      <StyledIconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, strategies[index])}
                      >
                        <MoreVertical size={16} />
                      </StyledIconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: colors.secondaryText, ml: 4 }}>
                    {opportunity.description}
                  </Typography>
                  {opportunity.progress > 0 && (
                    <Box sx={{ ml: 4, mt: 2, width: '50%' }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={opportunity.progress}
                        sx={{
                          backgroundColor: `${opportunity.color}15`,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: opportunity.color
                          }
                        }}
                      />
                    </Box>
                  )}
                </OpportunityCard>
              ))
            ) : (
              // Si no hay oportunidades
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: colors.secondaryText, mb: 2 }}>
                  No growth strategies found
                </Typography>
              </Box>
            )}

            {/* Botones de acción */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <ActionButton
                // 3) Cambiamos a la vista 'action-plan'
                onClick={() => setView('action-plan')}
                startIcon={<Target size={16} />}
              >
               Action Plan
              </ActionButton>
              <ActionButton
                onClick={handleGenerateAnalysis}
                disabled={analyzing}
                startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <RefreshCw size={16} />}
                variant="outlined"
              >
                Generate Analysis
              </ActionButton>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>

      {/* Menú de opciones */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setMenuAnchorEl(null);
          handleStrategyClick(selectedStrategy);
        }}>
          <List size={16} style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* Diálogo de detalles */}
      <StyledDialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedStrategy ? 'Strategy Details' : 'Create New Strategy'}
        </DialogTitle>
        <DialogContent>
          {/* Aquí iría el contenido del diálogo de detalles */}
        </DialogContent>
        <DialogActions>
          <DialogCloseButton onClick={() => setOpenDetailDialog(false)}>
            Close
          </DialogCloseButton>
        </DialogActions>
      </StyledDialog>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert
          onClose={() => setError(null)}
          severity={error?.includes('exitosamente') ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Strategy;
